/**
 * Karnöffel — pure JS port of the backend engine.
 *
 * Used by `KarnoffelTutorial.jsx` to run a local single-player game
 * against an AI opponent (no WebSocket / no Mongo).  Keep this in
 * sync with `/app/backend/games/karnoffel_engine.py` — the rule
 * vocabulary, trump hierarchy, and helpers are identical.
 */

export const SUITS = ["S", "H", "D", "C"];
export const RANKS = ["K", "Q", "J", "10", "9", "8", "7", "6", "5", "4", "3", "2"];

export const PLAIN_ORDER = {
  K: 12, Q: 11, J: 10, "10": 9, 9: 8, 8: 7,
  7: 6, 6: 5, 5: 4, 4: 3, 3: 2, 2: 1,
};

// Special-trump card power — higher wins.  Cards NOT listed here
// fall back to the "low-trump" tier (LOW_TRUMP_OFFSET + plain order).
export const TRUMP_POWER = {
  "2": 100, // Devil
  "7": 90,  // Karnöffel
  J:   80,  // Pope
  "6": 70,  // Kaiser
  "3": 60,
  "5": 50,
  "4": 40,
};
export const LOW_TRUMP_OFFSET = 10;

export const SPECIAL_NAMES = {
  "2": "Devil",
  "7": "Karnöffel",
  J:   "Pope",
  "6": "Kaiser",
};

export function buildDeck() {
  const out = [];
  for (const s of SUITS) for (const r of RANKS) out.push({ s, r, id: `${s}-${r}` });
  return out;
}

export function shuffledDeck() {
  const d = buildDeck();
  for (let i = d.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [d[i], d[j]] = [d[j], d[i]];
  }
  return d;
}

export function cardPower(card, trumpSuit, ledSuit) {
  if (card.s === trumpSuit) {
    if (TRUMP_POWER[card.r] !== undefined) return TRUMP_POWER[card.r];
    return LOW_TRUMP_OFFSET + PLAIN_ORDER[card.r];
  }
  if (ledSuit !== null && ledSuit !== undefined && card.s === ledSuit) {
    return PLAIN_ORDER[card.r];
  }
  return 0;
}

export function resolveTrick(ledCard, followCard, trumpSuit) {
  const pLed = cardPower(ledCard, trumpSuit, ledCard.s);
  const pFol = cardPower(followCard, trumpSuit, ledCard.s);
  return pFol > pLed ? 1 : 0;
}

export function canPlay(card, hand, ledSuit) {
  if (!ledSuit) return hand.some((c) => c.id === card.id);
  const hasSuit = hand.some((c) => c.s === ledSuit);
  if (hasSuit) return card.s === ledSuit;
  return hand.some((c) => c.id === card.id);
}

export function dealRound() {
  const deck = shuffledDeck();
  return {
    hands: { host: deck.slice(0, 5), guest: deck.slice(5, 10) },
    trump: deck[10].s,
    turnup: deck[10],
    stock: deck.slice(11),
    led: null,
    leader: "guest", // non-dealer leads first
    tricks: { host: 0, guest: 0 },
  };
}

// ---- AI policy ----------------------------------------------------------
// Tutorial-grade heuristics:
//   - If leading: play the LOWEST non-trump card in hand (saves trumps).
//   - If following: try to BEAT the led card with the LOWEST card that
//     does so.  If no card in hand beats it, slough the lowest legal
//     card (preferring non-trump non-led-suit cards to dump junk).
export function aiChoose(hand, deal, role) {
  const ledSuit = deal.led ? deal.led.card.s : null;
  // Legal moves first.
  const legal = hand.filter((c) => canPlay(c, hand, ledSuit));
  if (legal.length === 0) return hand[0]; // shouldn't happen
  if (!deal.led) {
    // Leading: pick the lowest non-trump if available; else lowest trump.
    const nonTrump = legal.filter((c) => c.s !== deal.trump);
    const pool = nonTrump.length ? nonTrump : legal;
    pool.sort(
      (a, b) => cardPower(a, deal.trump, null) - cardPower(b, deal.trump, null),
    );
    return pool[0];
  }
  // Following: find cards that beat the led card.
  const ledCard = deal.led.card;
  const winners = legal.filter(
    (c) => cardPower(c, deal.trump, ledSuit) > cardPower(ledCard, deal.trump, ledSuit),
  );
  if (winners.length > 0) {
    winners.sort(
      (a, b) => cardPower(a, deal.trump, ledSuit) - cardPower(b, deal.trump, ledSuit),
    );
    return winners[0];
  }
  // Slough lowest legal card.
  legal.sort(
    (a, b) => cardPower(a, deal.trump, ledSuit) - cardPower(b, deal.trump, ledSuit),
  );
  return legal[0];
}
